import { Button, Flex, HStack, Text, VStack, VisuallyHidden } from '@chakra-ui/react';
import { useEffect, useRef } from 'react';
import { useWorkspaceId } from '../../contexts/WorkspaceContext';
import { useAppToast } from '../../hooks/ui/useAppToast';
import { trackClipboardCopyFailed } from '../../utils/analytics';
import { AppInstallQR } from '../ui/AppInstallQR';
import { MaterialIcon } from '../ui/MaterialIcon';
import { MobileAccessQR, buildMobileAccessUrl } from '../ui/MobileAccessQR';
import { buildEquipmentCapturePath } from './equipmentCapturePath';

/**
 * "Photograph it with your phone": the app QR first, as on the Create modal's
 * Phone tab, with the browser QR (which opens the phone camera on the
 * equipment intent) one tap away. Its "QR Displayed" event fires from
 * `AddEquipmentPanel`, once per open.
 */
export function AddEquipmentQrStep({ onBack }: { readonly onBack: () => void }) {
  const toast = useAppToast();
  const workspaceId = useWorkspaceId();
  const headingRef = useRef<HTMLParagraphElement>(null);
  const path = buildEquipmentCapturePath(workspaceId);

  // The activating button unmounted with the step swap; without this focus
  // falls to the close button and a screen reader hears nothing.
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const copyLink = () => {
    // navigator.clipboard is undefined outside a secure context, where
    // `.writeText(...)` throws SYNCHRONOUSLY, past the .catch below. An
    // optional chain would swallow the toast with the throw, so the guard is
    // an explicit branch.
    const url = buildMobileAccessUrl(path);
    if (!navigator.clipboard) {
      trackClipboardCopyFailed('equipment_qr_link', 'unavailable');
      toast({ title: 'Could not copy link', status: 'error' });
      return;
    }
    void navigator.clipboard
      .writeText(url)
      .then(() => toast({ title: 'Link copied', status: 'success' }))
      .catch(() => {
        trackClipboardCopyFailed('equipment_qr_link', 'rejected');
        toast({ title: 'Could not copy link', status: 'error' });
      });
  };

  return (
    <Flex direction="column" flex={1} minH={0}>
      {/* The content scrolls; the Back row stays pinned below it. `p`/`m`
          keep focus rings inside the scroller's clip. */}
      <VStack spacing={4} align="stretch" flex={1} minH={0} overflowY="auto" p={1} m={-1}>
        <Text fontSize="sm" color="text.secondary" tabIndex={-1} ref={headingRef}>
          Scan to open Bower on your phone, then photograph the instrument&apos;s label. It is saved
          to your equipment.
        </Text>
        <AppInstallQR
          src="equipment_qr"
          size={180}
          browserFallback={
            <VStack spacing={3} align="center">
              <MobileAccessQR
                size={180}
                path={path}
                caption="Scan to photograph equipment in your phone's browser"
              />
              {/* A QR is visual-only, so the URL stays in the DOM as its text
                equivalent; visually hidden because a wrapped 130-character URL
                told a sighted user nothing. The Copy button is the sighted path. */}
              <VisuallyHidden>{buildMobileAccessUrl(path)}</VisuallyHidden>
              <Button
                size="sm"
                variant="outline"
                leftIcon={<MaterialIcon name="link" boxSize="16px" />}
                onClick={copyLink}
              >
                Copy the link instead
              </Button>
              <Text fontSize="xs" color="text.muted" textAlign="center">
                You&apos;ll be asked to sign in on your phone the first time.
              </Text>
            </VStack>
          }
        />
      </VStack>
      <HStack justify="flex-end" pt={4} flexShrink={0}>
        <Button variant="outline" size="sm" onClick={onBack}>
          Back
        </Button>
      </HStack>
    </Flex>
  );
}
